import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowRight, Bell, BellRing } from 'lucide-react'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useRecentNotifications,
  useUnreadCount,
} from '@/lib/notificationQueries'
import { groupByDay, type AppNotification } from '@/lib/notifications'
import { ApiError } from '@/lib/api'
import {
  Button,
  EmptyState,
  ErrorState,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import { NotificationDayHeading, NotificationItem } from '@/components/NotificationItem'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n/I18nProvider'

/** Where the bell's "Sva obavještenja" goes. */
const CENTRE_PATH = '/obavijesti'

/**
 * The bell in the header, and the panel it opens.
 *
 * No props, on purpose: the shell places it and nothing else. Everything it
 * shows — the count, the list, what each row offers — it fetches itself.
 */
export function NotificationBell() {
  const { t, tp } = useI18n()
  const [open, setOpen] = useState(false)
  const unread = useUnreadCount()
  const { total, high } = unread.data ?? { total: 0, high: 0 }

  // The count belongs in the label, not just the badge — a screen reader user
  // gets nothing from a coloured dot. Urgent ones are named separately because
  // they are the reason to open the panel now rather than later.
  const label =
    total > 0
      ? [
          `${t('notifications.title')} — ${tp('notifications.unread', total)}`,
          high > 0 ? tp('notifications.high', high) : null,
        ]
          .filter(Boolean)
          .join(', ')
      : t('notifications.title')

  const Icon = high > 0 ? BellRing : Bell

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              onClick={() => setOpen(true)}
              className={cn(
                'focus-visible:ring-ring/50 relative flex size-control items-center justify-center rounded-xl transition-colors focus-visible:ring-2 focus-visible:outline-none',
                // Smaller under a mouse, like the rest of the header; a finger
                // in a glove keeps the full 44px.
                'pointer-fine:size-9',
                /*
                 * A stronger bell while something urgent is unread.
                 *
                 * The badge alone could not say it: "3" reads the same whether
                 * the three are status changes or a machine down on the night
                 * shift. A ringing bell in red can, from across the room.
                 */
                high > 0
                  ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/70'
                  : 'hover:bg-muted',
              )}
              aria-label={label}
            />
          }
        >
          <Icon className="size-5" aria-hidden />

          {total > 0 && (
            <span
              className={cn(
                'absolute top-1.5 right-1.5 flex min-w-4.5 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white pointer-fine:top-0.5 pointer-fine:right-0.5',
                high > 0 ? 'bg-red-600' : 'bg-brand-600',
              )}
              aria-hidden
            >
              {total > 9 ? '9+' : total}
            </span>
          )}
        </TooltipTrigger>

        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>

      <NotificationPanel
        open={open}
        onClose={() => setOpen(false)}
        unreadCount={total}
        highCount={high}
      />
    </>
  )
}

function NotificationPanel({
  open,
  onClose,
  unreadCount,
  highCount,
}: {
  open: boolean
  onClose: () => void
  unreadCount: number
  highCount: number
}) {
  const { t, tp } = useI18n()
  const navigate = useNavigate()
  // Only fetched — and polled — while the panel is open.
  const query = useRecentNotifications(open)
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  const notifications = query.data?.data ?? []

  const failed = (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : t('notifications.failed'))

  function handleAction(notification: AppNotification) {
    if (!notification.isRead) markRead.mutate(notification.id)
    if (!notification.action) return

    onClose()
    navigate(notification.action.path)
  }

  function handleMarkRead(notification: AppNotification) {
    markRead.mutate(notification.id, { onError: failed })
  }

  function openCentre() {
    onClose()
    navigate(CENTRE_PATH)
  }

  return (
    /*
     * A side panel rather than a centred dialog.
     *
     * Notifications are a list you skim and dismiss, not a task you complete —
     * a box in the middle of the screen covering the page you were reading is
     * the wrong shape for that. The sheet slides in beside the content, is
     * full-height so more than three fit without scrolling, and on a phone it
     * covers the screen the way a notification drawer already does.
     */
    <Sheet open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full flex-col gap-0 p-0 sm:max-w-md',
          // Glass, so the page you were reading stays faintly visible behind
          // the panel rather than being replaced by it.
          'supports-[backdrop-filter]:bg-popover/85 backdrop-blur-2xl backdrop-saturate-150',
          'border-black/5 dark:border-white/10',
        )}
        aria-label={t('notifications.title')}
      >
        <SheetHeader className="border-b px-4 py-3">
          <SheetTitle>{t('notifications.title')}</SheetTitle>
          {/*
            Counts by importance: how many, and how many of those cannot wait.
            The urgent count is the one in colour, so it is the one the eye
            lands on.
          */}
          <SheetDescription className="flex flex-wrap items-center gap-1.5">
            {unreadCount > 0 ? (
              <>
                <span>{tp('notifications.unread', unreadCount)}</span>
                {highCount > 0 && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/60 dark:text-red-300">
                    {tp('notifications.high', highCount)}
                  </span>
                )}
              </>
            ) : (
              t('notifications.allRead')
            )}
          </SheetDescription>
        </SheetHeader>

        {/* min-h-0 so this actually scrolls instead of pushing the footer off. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {query.isLoading && <Spinner />}

          {query.isError && !query.data && (
            <ErrorState message={(query.error as Error).message} onRetry={() => query.refetch()} />
          )}

          {query.data && notifications.length === 0 && (
            <EmptyState
              icon={<Bell className="size-8" />}
              title={t('notifications.empty')}
              description={t('notifications.emptyHint')}
            />
          )}

          {groupByDay(notifications).map((group) => (
            <section key={group.key}>
              {/* Sticky, so you always know which day you are looking at while
                  scrolling back through a busy week. */}
              <NotificationDayHeading
                group={group}
                className="bg-background sticky top-0 z-10 px-2"
              />

              <ul className="space-y-1 pb-2">
                {group.items.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onAction={handleAction}
                    onMarkRead={handleMarkRead}
                    compact
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>

        <SheetFooter className="mx-0 mb-0 flex-row flex-wrap gap-2 border-t p-3">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() =>
              markAll.mutate(undefined, {
                onSuccess: (reply) => reply?.message && toast.success(reply.message),
                onError: failed,
              })
            }
            loading={markAll.isPending}
            disabled={unreadCount === 0}
          >
            {t('notifications.markAllRead')}
          </Button>

          <Button variant="ghost" className="flex-1" onClick={openCentre}>
            {t('notifications.viewAll')}
            <ArrowRight className="size-4" aria-hidden />
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
